import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Reminder types
export type ReminderType = 'sponsor-setup';

interface ReminderState {
  // Track when onboarding was completed
  onboardingCompletedAt: number | null;

  // Track dismissed reminders (by type and timestamp of dismissal)
  dismissedReminders: Record<ReminderType, number | null>;

  // Track if user skipped sponsor during onboarding
  skippedSponsorDuringOnboarding: boolean;

  // Actions
  markOnboardingCompleted: () => void;
  markSponsorSkipped: (skipped: boolean) => void;
  dismissReminder: (type: ReminderType) => void;
  resetReminders: () => void;

  // Queries
  shouldShowReminder: (type: ReminderType) => boolean;
  getDaysSinceOnboarding: () => number | null;
}

// How many days to wait before showing sponsor reminder
const SPONSOR_REMINDER_DELAY_DAYS = 2;

// How many days before showing the reminder again after dismissal
const REMINDER_SNOOZE_DAYS = 3;

export const useReminderStore = create<ReminderState>()(
  persist(
    (set, get) => ({
      onboardingCompletedAt: null,
      dismissedReminders: {
        'sponsor-setup': null,
      },
      skippedSponsorDuringOnboarding: false,

      markOnboardingCompleted: () => {
        set({ onboardingCompletedAt: Date.now() });
      },

      markSponsorSkipped: (skipped) => {
        set({ skippedSponsorDuringOnboarding: skipped });
      },

      dismissReminder: (type) => {
        set((state) => ({
          dismissedReminders: {
            ...state.dismissedReminders,
            [type]: Date.now(),
          },
        }));
      },

      resetReminders: () => {
        set({
          onboardingCompletedAt: null,
          dismissedReminders: {
            'sponsor-setup': null,
          },
          skippedSponsorDuringOnboarding: false,
        });
      },

      shouldShowReminder: (type) => {
        const state = get();

        if (type === 'sponsor-setup') {
          // Don't show if user didn't skip sponsor
          if (!state.skippedSponsorDuringOnboarding) {
            return false;
          }

          // Don't show if onboarding not completed
          if (!state.onboardingCompletedAt) {
            return false;
          }

          const daysSinceOnboarding = state.getDaysSinceOnboarding();
          if (daysSinceOnboarding === null || daysSinceOnboarding < SPONSOR_REMINDER_DELAY_DAYS) {
            return false;
          }

          // Check if dismissed
          const dismissedAt = state.dismissedReminders['sponsor-setup'];
          if (dismissedAt) {
            const daysSinceDismissal = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
            // If dismissed recently, don't show
            if (daysSinceDismissal < REMINDER_SNOOZE_DAYS) {
              return false;
            }
          }

          return true;
        }

        return false;
      },

      getDaysSinceOnboarding: () => {
        const { onboardingCompletedAt } = get();
        if (!onboardingCompletedAt) return null;
        return Math.floor((Date.now() - onboardingCompletedAt) / (1000 * 60 * 60 * 24));
      },
    }),
    {
      name: 'reprieve-reminders',
      partialize: (state) => ({
        onboardingCompletedAt: state.onboardingCompletedAt,
        dismissedReminders: state.dismissedReminders,
        skippedSponsorDuringOnboarding: state.skippedSponsorDuringOnboarding,
      }),
    }
  )
);

// Hook for easy access
export function useReminders() {
  return useReminderStore();
}
